import { getFormValues, getFormSyncErrors } from 'redux-form';

import { dispatch, getState } from '../../store/store';
import HttpService from '../../services/httpService';

import CommandsActions from './commands/commandsActions';

import FormActions from './utils/formActions';
import FormsSelectors from './utils/formsSelectors';
import FormsConfigs from './formsConfigs/formsConfigs';

import FormUtils from '../../utils/formUtils';

import formsTimers from './utils/formsTimers';
import formsCache from './utils/formsCache';

const syncFields = ({ form, formConfig }) => {
  if (!form || !formConfig) return;

  const fieldsConfig = formConfig.fields;
  Object.keys(fieldsConfig).forEach(key => {
    const config = fieldsConfig[key];
    if (config && config.value) {
      HttpService.put(`${form.instanceName}/stateUpdate`, {
        instanceToken: form.instanceToken,
        submitField: key,
        fields: {
          [key]: { value: config.value }
        }
      });
    }
  });
};

const getFormFields = (formFields, formConfig) => {
  const fields = formFields;
  if (!formConfig) return fields;

  const fieldsConfig = formConfig.fields;

  Object.keys(fieldsConfig).forEach(key => {
    const field = formFields[key];
    const fieldConfig = fieldsConfig[key];

    if (!field) return;

    fields[key] = {
      ...field,
      value:
        fieldConfig && typeof fieldConfig.value !== 'undefined'
          ? fieldConfig.value
          : field.value,
      disabled:
        fieldConfig && typeof fieldConfig.disabled !== 'undefined'
          ? fieldConfig.disabled
          : field.disabled
    };

    if (fieldConfig.type === 'grid' && fieldConfig.gridConfig) {
      const { columns } = fieldConfig.gridConfig;

      const extendedColumns = [];
      Object.keys(columns).forEach(columnKey => {
        if (columns[columnKey].extend) {
          extendedColumns.push({
            id: columnKey
          });
        }
      });

      fields[key].columns = field.columns
        .concat(extendedColumns)
        .map(column => ({
          id: column.id,
          active: column.type === 'BOOLEAN_ACTIVE',
          ...columns[column.id]
        }))
        .sort((column1, column2) => column1.order - column2.order);

      fields[key].items = fieldConfig.gridConfig.map
        ? fieldConfig.gridConfig.map(field.items, fields)
        : field.items;
    }
  });

  return fields;
};

const initById = ({ id }) => {
  const config = FormsConfigs.getById(id);
  if (!config) return;

  const { instanceName } = config;

  if (!instanceName) return;

  CommandsActions.apply({
    request: () => HttpService.get(`${instanceName}/formInit`, {})
  });
};

const init = async ({ form }) => {
  const formConfig =
    form.type === 'grid' ? null : FormsConfigs.getByName(form.id);

  const formId = `FORM_${form.instanceName}_${form.instanceToken}`;
  const formFields = getFormFields(form.fields, formConfig);

  formsCache.setServerFormState({
    instanceToken: form.instanceToken,
    fields: formFields
  });

  syncFields({
    form,
    formConfig
  });
  dispatch({
    type: 'FORM_INIT',
    form: {
      ...form,
      formId,
      config: formConfig,
      fields: formFields
    }
  });
};

const close = async ({ instanceToken }) => {
  formsCache.deleteServerFormState(instanceToken);

  dispatch({
    type: 'FORM_CLOSE',
    instanceToken
  });
};

const update = async form => {
  dispatch({
    type: 'FORM_UPDATE',
    form
  });

  const updatedForm = FormsSelectors.getByToken(form.instanceToken);
  if (!updatedForm) return;

  const formValues = FormUtils.getFormValues(
    form.fields,
    updatedForm.config.fields
  );

  FormActions.change(updatedForm.formId, formValues);
  formsCache.setServerFormState({
    instanceToken: form.instanceToken,
    values: formValues
  });
};

const onChangeField = async ({ instanceToken, fieldId, fieldValue }) => {
  const form = FormsSelectors.getByToken(instanceToken);
  if (!form) return null;

  const formFields = form.fields;
  const formValues = getFormValues(form.formId)(getState());
  const formErrors = getFormSyncErrors(form.formId)(getState());

  const apiValues = FormUtils.getApiValues(
    formFields,
    formValues,
    form.config.fields,
    formErrors
  );

  const updatedFields = formsCache.getNotUpdatedServerFormFields(
    form.instanceToken,
    apiValues
  );

  formsCache.setServerFormState({
    instanceToken: form.instanceName,
    fields: updatedFields
  });

  const response = await CommandsActions.apply({
    instanceToken,
    request: () =>
      HttpService.put(`${form.instanceName}/stateUpdate`, {
        instanceToken: form.instanceToken,
        submitField: fieldId,
        fields: {
          ...updatedFields,
          [fieldId]: { value: fieldValue }
        }
      })
  });

  return response;
};

const updateForm = form => {
  dispatch({
    type: 'FORM_UPDATE',
    form
  });
};

const setIsActionProcessing = (instanceToken, actionId, isProcessing) => {
  updateForm({
    instanceToken,
    fields: {},
    actions: {
      [actionId]: {
        isProcessing
      }
    }
  });
};

const apply = async ({
  instanceToken,
  instanceName,
  actionId,
  extActionId,
  requestParams,
  loaderParams,
  signSource,
  signInstanceName,
  ...restParams
}) => {
  const form = FormsSelectors.getByToken(instanceToken);
  if (!form) return null;

  const formInstanceName = instanceName || form.instanceName;

  setIsActionProcessing(instanceToken, actionId, true);

  const request = () =>
    HttpService.put(`${formInstanceName}/doAction`, {
      instanceToken,
      actionId,
      extActionId,
      ...requestParams
    });

  const result = await CommandsActions.apply({
    instanceToken,
    actionId,
    requestParams,
    loaderParams,
    signSource: signSource || 'form',
    signInstanceName: signInstanceName || formInstanceName,
    ...restParams,
    request
  });

  setIsActionProcessing(instanceToken, actionId, false);

  return result;
};

const FormsActions = {
  initById,
  init,
  close,
  update,

  onChangeField,

  apply,

  updateForm,

  addTimer: formsTimers.addTimer,
  isTimerExist: formsTimers.isTimerExist,
  clearTimer: formsTimers.clearTimer,
  clearTimers: formsTimers.clearTimers
};

export default FormsActions;



// WEBPACK FOOTER //
// ./src/store/forms/formsActions.js